import { useEffect, useState } from "react";
import axios from "axios";
import { Play, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RecentMusic = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // id of expanded player

  useEffect(() => {
    axios
      .get(`${API}/recent-tracks`)
      .then((r) => {
        setTracks(r.data);
        if (r.data[0]) setActive(r.data[0].id);
      })
      .catch(() => setTracks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="recent"
      data-testid="recent-music-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
                Now Playing
              </span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
              Recent Music
            </h2>
            <p className="mt-4 text-white/60 max-w-lg">
              Straight from the feed. Press play, stay a while — or tap Spotify
              below for the mainline catalogue.
            </p>
          </div>
          <a
            data-testid="recent-view-all"
            href="https://www.bandlab.com/tekreck"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#D4AF37] transition-colors"
          >
            View full feed on BandLab
            <ExternalLink size={14} />
          </a>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-white/50 py-8">
            <Loader2 size={16} className="animate-spin" /> Loading tracks…
          </div>
        ) : tracks.length === 0 ? (
          <p className="text-white/50 py-8">No recent tracks yet.</p>
        ) : (
          <div className="space-y-4">
            {tracks.map((t, i) => {
              const isActive = active === t.id;
              return (
                <div
                  key={t.id}
                  data-testid={`recent-track-${i}`}
                  className={`border transition-colors ${
                    isActive
                      ? "border-[#D4AF37]/60 bg-[#0e0e0e]"
                      : "border-white/10 bg-[#080808] hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center">
                    <button
                      data-testid={`recent-track-toggle-${i}`}
                      onClick={() => setActive(isActive ? null : t.id)}
                      className="flex-1 flex items-center gap-6 px-5 py-4 text-left"
                    >
                      <span className="font-display text-white/30 text-sm w-8">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={`w-10 h-10 flex items-center justify-center border transition-colors ${
                          isActive
                            ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                            : "border-white/15 text-white"
                        }`}
                      >
                        <Play size={14} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block font-display text-lg md:text-xl truncate transition-colors ${
                            isActive ? "text-[#D4AF37]" : ""
                          }`}
                        >
                          {t.title}
                        </span>
                        {t.album && (
                          <span className="block text-xs uppercase tracking-[0.2em] text-white/40 mt-0.5">
                            {t.album}
                          </span>
                        )}
                      </span>
                      {t.length && (
                        <span className="text-sm text-white/50 tabular-nums">
                          {t.length}
                        </span>
                      )}
                    </button>
                    {t.url && (
                      <a
                        data-testid={`recent-track-external-${i}`}
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        className="px-5 py-4 text-white/40 hover:text-[#D4AF37] flex items-center"
                        title="Open on BandLab"
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>

                  {isActive && t.embed_url && (
                    <div
                      data-testid={`recent-track-player-${i}`}
                      className="px-2 pb-3"
                    >
                      <iframe
                        title={t.title}
                        src={t.embed_url}
                        width="100%"
                        height="160"
                        frameBorder="0"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        loading="lazy"
                        style={{ display: "block", background: "#000" }}
                      />
                    </div>
                  )}
                  {isActive && !t.embed_url && t.url && (
                    <div className="px-5 pb-4 text-xs text-white/50">
                      No embed available.{" "}
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#D4AF37] underline"
                      >
                        Open on BandLab
                      </a>
                      .
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Spotify embed kept as a secondary catalogue */}
        <div className="mt-12 border border-white/10">
          <iframe
            data-testid="spotify-embed"
            title="Tekreck on Spotify"
            src="https://open.spotify.com/embed/artist/7x9ldSU6voBZpcHMNOGVlw?utm_source=generator&theme=0"
            width="100%"
            height="352"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            style={{ display: "block" }}
          />
        </div>
      </div>
    </section>
  );
};
